import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import type {UserSettingsOptionConfig} from '@github-ui/issue-create/getSafeConfig'
import type {HeaderAction} from '@github-ui/layout-primitives/use-header-actions-overflow'
import {noop} from '@github-ui/noop'
import {Link} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {ActionList, Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import type {ToggleMilestoneState$key} from './__generated__/ToggleMilestoneState.graphql'
import {LABELS} from './constants/labels'
import {useToggleMilestoneState} from './ToggleMilestoneState'

type UseMilestoneHeaderActionsArgs = {
  viewerCanPush: boolean
  milestoneRef: ToggleMilestoneState$key
  milestoneNumber: number
  milestoneTitle: string
  ownerLogin: string
  repoName: string
  optionConfig?: UserSettingsOptionConfig
}

/**
 * Builds milestone-detail header actions for `FilteredListPageLayout.HeaderActions`,
 * which keeps `New issue` inline and moves the rest to an overflow kebab so the
 * actions cell doesn't crowd out the title.
 */
export function useMilestoneHeaderActions({
  viewerCanPush,
  milestoneRef,
  milestoneNumber,
  milestoneTitle,
  ownerLogin,
  repoName,
  optionConfig,
}: UseMilestoneHeaderActionsArgs): HeaderAction[] {
  const navigate = useNavigate()
  const toggle = useToggleMilestoneState(milestoneRef)

  const navigateToMilestoneFn = useCallback(
    (url: string) => {
      if (!milestoneTitle) return
      const milestoneParam = `milestone=${encodeURIComponent(milestoneTitle)}`
      const urlWithMilestone = url.includes('?') ? `${url}&${milestoneParam}` : `${url}?${milestoneParam}`
      navigate(urlWithMilestone)
    },
    [milestoneTitle, navigate],
  )

  const editMilestoneUrl = `/${ownerLogin}/${repoName}/milestones/${milestoneNumber}/edit`

  return useMemo<HeaderAction[]>(() => {
    const actions: HeaderAction[] = []

    if (viewerCanPush) {
      actions.push({
        key: 'edit-milestone',
        renderInline: () => (
          <Button as={Link} to={editMilestoneUrl}>
            {LABELS.editMilestone}
          </Button>
        ),
        renderMenuItem: () => (
          <ActionList.LinkItem as={Link} to={editMilestoneUrl}>
            {LABELS.editMilestone}
          </ActionList.LinkItem>
        ),
      })

      actions.push({
        key: 'toggle-milestone-state',
        renderInline: () => (
          <Button onClick={toggle.onClick} variant="default">
            {toggle.label}
          </Button>
        ),
        renderMenuItem: () => <ActionList.Item onSelect={toggle.onClick}>{toggle.label}</ActionList.Item>,
      })
    }

    actions.push({
      key: 'new-issue',
      alwaysInline: true,
      renderInline: () => (
        <CreateIssueButton
          label={LABELS.newIssue}
          navigate={navigateToMilestoneFn}
          optionConfig={{
            ...optionConfig,
            showRepositoryPicker: false,
            issueCreateArguments: {
              repository: {
                owner: ownerLogin,
                name: repoName,
              },
            },
            showFullScreenButton: true,
            navigateToFullScreenOnTemplateChoice: navigate !== noop,
            canBypassTemplateSelection: true,
          }}
        />
      ),
      // `alwaysInline`, but the type still requires a menu renderer.
      renderMenuItem: () => null,
    })

    return actions
  }, [
    viewerCanPush,
    editMilestoneUrl,
    toggle.onClick,
    toggle.label,
    navigateToMilestoneFn,
    optionConfig,
    ownerLogin,
    repoName,
    navigate,
  ])
}
